import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence, useReducedMotion } from 'framer-motion';
import { LoaderIcon } from 'lucide-react';
import { Header } from '../components/Header';
import { Footer } from '../components/Footer';
import { TrackingNotice } from '../components/TrackingNotice';
import { SchoolMatchCard } from '../components/SchoolMatchCard';
import { MatchProgress } from '../components/match/MatchProgress';
import { usePageMeta } from '../hooks/usePageMeta';
import { trackLeadConversion } from '../utils/conversion';
import { normalizeSchool } from '../utils/schoolFields';
import { clicksSearch, getBrandName, leadsPost, type ContactProfile, type MMSchool, type SearchProfile } from '../services/mediaMatchers';
interface CarouselState {
  leads?: MMSchool[];
  clicks?: MMSchool[];
  profile?: SearchProfile;
  contact?: ContactProfile;
  disclosure?: string;
  excludeBrand?: string;
}
export function MatchCarouselPage() {
  usePageMeta('Your School Matches | FindOnlineColleges', 'Review the accredited schools matched to your goals and choose which ones may contact you.');
  const navigate = useNavigate();
  const location = useLocation();
  const reduceMotion = useReducedMotion();
  const state = (location.state || {}) as CarouselState;
  const leads = useMemo(() => state.leads || [], [state.leads]);
  const contact = state.contact;
  const [clicks, setClicks] = useState<MMSchool[]>(state.clicks || []);
  const [clicksPending, setClicksPending] = useState(!state.clicks);

  // Phase 1 walks the Leads results, phase 2 the Clicks results.
  const [phase, setPhase] = useState<'leads' | 'clicks'>(leads.length > 0 ? 'leads' : 'clicks');
  const [index, setIndex] = useState(0);
  const [consented, setConsented] = useState(false);
  const [consentError, setConsentError] = useState('');
  const [posting, setPosting] = useState(false);
  const [acceptedCount, setAcceptedCount] = useState(0);
  const exitFlow = useCallback((count: number) => {
    navigate('/thank-you', {
      replace: true,
      state: {
        formData: contact,
        matchCount: count
      }
    });
  }, [navigate, contact]);

  // Nothing to show without a submitted form — send them back to the start.
  useEffect(() => {
    if (!contact) navigate('/get-matched', {
      replace: true
    });
  }, [contact, navigate]);

  // The Clicks Search may still have been in flight when we navigated here.
  useEffect(() => {
    if (state.clicks || !state.profile || !contact) return;
    let active = true;
    clicksSearch(state.profile, contact, state.excludeBrand).then(result => {
      if (!active) return;
      setClicks(result || []);
      setClicksPending(false);
    });
    return () => {
      active = false;
    };
  }, [state.clicks, state.profile, state.excludeBrand, contact]);

  // Both lists exhausted (or empty) — leave the funnel.
  const exhausted = phase === 'clicks' && !clicksPending && clicks.length - index <= 0;
  useEffect(() => {
    if (exhausted && contact) exitFlow(acceptedCount);
  }, [exhausted, contact, exitFlow, acceptedCount]);
  const advance = useCallback(() => {
    setConsented(false);
    setConsentError('');
    if (phase === 'leads') {
      if (index + 1 < leads.length) {
        setIndex(index + 1);
      } else {
        setPhase('clicks');
        setIndex(0);
      }
      return;
    }
    // Past the last click result the exhaustion effect redirects out.
    setIndex(index + 1);
  }, [phase, index, leads.length]);
  const handleAgree = async () => {
    const raw = leads[index];
    if (!raw || !contact || posting) return;
    if (!consented) {
      setConsentError('Please check the box to agree before continuing.');
      return;
    }
    setPosting(true);
    // The conversion only counts once the Post confirms.
    const ok = await leadsPost(raw, contact);
    if (ok) {
      setAcceptedCount(n => n + 1);
      trackLeadConversion({
        source: 'leads',
        brand: getBrandName(raw)
      });
    }
    setPosting(false);
    advance();
  };
  const handleClickThrough = () => {
    const raw = clicks[index];
    const url = raw ? normalizeSchool(raw).clickUrl : '';
    if (url) window.open(url, '_blank', 'noopener,noreferrer');
    advance();
  };
  if (!contact) return null;
  const isLeadsPhase = phase === 'leads';
  const raw = isLeadsPhase ? leads[index] : clicks[index];
  const total = isLeadsPhase ? leads.length : clicks.length;

  // Waiting on the Clicks Search, with no Leads left to show meanwhile.
  if (!raw && !isLeadsPhase && clicksPending) {
    return <div className="min-h-screen flex flex-col bg-gray-50" data-id="element-2114" data-mp="1xnalbar00erk">
        <Header data-id="element-2115" data-mp="1tmqo676fdb5f" />
        <main className="flex-1 flex flex-col items-center justify-center px-4" data-id="element-2116" data-mp="1tna9zyz74joq">
          <LoaderIcon className="w-8 h-8 text-crimson animate-spin mb-4" data-id="element-2117" data-mp="2q4mz4gdcc3ws" />
          <p className="text-gray-600 font-medium" data-id="element-2118" data-mp="2q6rblmpb2b8o">
            Finding more programs for you...
          </p>
        </main>
        <Footer data-id="element-2119" data-mp="7vad3u9gnqrf" />
      </div>;
  }
  if (!raw) return null;
  const school = normalizeSchool(raw);
  return <div className="min-h-screen flex flex-col bg-gray-50" data-id="element-2120" data-mp="ctv9tjpi7sb4">
      <Header data-id="element-2121" data-mp="cwhixns1mfvg" />
      <main className="flex-1 py-8 sm:py-12 px-4" data-id="element-2122" data-mp="gvynmeznb8zk">
        <div className="max-w-[640px] mx-auto" data-id="element-2123" data-mp="gv75a5v277r8">
          <MatchProgress eyebrow={isLeadsPhase ? undefined : 'Additional matches'} current={index + 1} total={total} data-id="element-2124" data-mp="lv1w69eu3xca" />

          <AnimatePresence mode="wait" data-id="element-2125" data-mp="qtwc93lf37sp">
            <motion.div key={`${phase}-${index}`} initial={{
            opacity: 0,
            y: reduceMotion ? 0 : 8
          }} animate={{
            opacity: 1,
            y: 0
          }} exit={{
            opacity: 0,
            y: reduceMotion ? 0 : -8
          }} transition={{
            duration: 0.18,
            ease: [0.23, 1, 0.32, 1]
          }} className="mt-5" data-id="element-2126" data-mp="vszjmuovdq3u">
              <SchoolMatchCard school={school} variant={isLeadsPhase ? 'lead' : 'click'} consented={consented} onConsentChange={isLeadsPhase ? next => {
              setConsented(next);
              if (next) setConsentError('');
            } : undefined} consentError={consentError} fallbackDisclosure={state.disclosure || `By checking this box, I provide my express written consent for ${school.name} to contact me by phone, text, and email about educational opportunities, including through automated technology at the number and email I provided. Consent is not a condition of enrollment, and I may revoke it at any time.`} isSubmitting={posting} onPrimary={isLeadsPhase ? handleAgree : handleClickThrough} onSkip={advance} data-id="element-2127" data-mp="19m76ya5x3zfq" />
            </motion.div>
          </AnimatePresence>

          {/* A visitor who wants out shouldn't have to skip one school at a time. */}
          <div className="mt-5 text-center" data-id="element-2128" data-mp="jhpf54pkfvwk">
            <button type="button" onClick={() => exitFlow(acceptedCount)} disabled={posting} className="text-sm text-gray-500 hover:text-gray-700 hover:underline transition-colors duration-200" data-id="element-2129" data-mp="ofhdaggt0e97">
              Skip all remaining &rarr;
            </button>
          </div>

          <div className="mt-8" data-id="element-2130" data-mp="ovab3lnpk62">
            <TrackingNotice data-id="element-2131" data-mp="rzisd65n22j" />
          </div>
        </div>
      </main>
      <Footer data-id="element-2132" data-mp="5pr8d0jbuc3k" />
    </div>;
}